import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from './config/constants';
import { BootScene, MenuScene, ResultsScene } from './scenes/MenuScene';
import { BattleScene } from './scenes/BattleScene';
import { CommandScene } from './scenes/CommandScene';
import { PauseScene } from './scenes/PauseScene';
import { MapForgeScene } from './scenes/MapForgeScene';
import { applyAudioSettings } from './core/Settings';
import { saveManager } from './core/SaveManager';
import { MAPS } from './data/map';
import { ENEMIES } from './data/enemies';
import { audio } from './core/AudioSynth';
import { music } from './core/MusicSystem';
import { voice } from './core/Voice';
import { localMusic } from './core/LocalMusicPlayer';
import { RuntimeAssetsScene } from './scenes/RuntimeAssetsScene';
import { StoryScene } from './scenes/StoryScene';
import { MISSIONS, isMissionUnlocked } from './data/missions';

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'app',
  width: GAME_WIDTH,
  height: GAME_HEIGHT,
  backgroundColor: '#07090d',
  scale: {
    // Keep the authored 16:9 HUD fully visible. Battlefield plates handle
    // their own small crop, so no top/bottom controls disappear on ultrawide.
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    expandParent: true,
  },
  scene: [BootScene, MenuScene, StoryScene, RuntimeAssetsScene, CommandScene, MapForgeScene, BattleScene, PauseScene, ResultsScene],
  fps: {
    target: 60,
    smoothStep: true,
  },
  render: {
    antialias: true,
    roundPixels: false,
  },
};

const game = new Phaser.Game(config);

// Expose for tooling/playtests.
(window as unknown as { __game?: Phaser.Game }).__game = game;
(window as unknown as { __save?: unknown }).__save = saveManager;
(window as unknown as { __maps?: unknown }).__maps = MAPS;
(window as unknown as { __enemies?: unknown }).__enemies = ENEMIES;
(window as unknown as { __music?: unknown }).__music = music;
(window as unknown as { __audio?: unknown }).__audio = audio;
(window as unknown as { __voice?: unknown }).__voice = voice;
(window as unknown as { __localMusic?: unknown }).__localMusic = localMusic;

applyAudioSettings();

/** Keep the generated cursor legible without making the default pointer oversized. */
const applyCursorSize = () => {
  const value = Number(saveManager.data.settings.cursorSize ?? 0.75);
  // The previous "small" cursor was a 64px asset and felt oversized in
  // combat. That artwork is now the upper limit; default to a tighter size.
  const preset = value >= 0.9
    ? { file: 'large', hotspot: '9 6' }
    : value >= 0.7
      ? { file: 'medium', hotspot: '6 4' }
      : { file: 'small', hotspot: '4 3' };
  document.documentElement.style.setProperty('--apogee-cursor', `url('/assets/art/ui/apogee-cursor-chrome-${preset.file}.png') ${preset.hotspot}, auto`);
};
applyCursorSize();
window.addEventListener('apogee-cursor-size', applyCursorSize);

/**
 * Canvas controls are necessarily tiny on a phone because the game keeps its
 * authored 16:9 coordinate system. This lightweight dock stays in CSS pixels,
 * so fullscreen, HUD scale, and colour boost remain easy to reach on touch.
 */
if (typeof document !== 'undefined') {
  const mobileQuery = window.matchMedia('(max-width: 900px), (pointer: coarse)');
  const dock = document.createElement('div');
  dock.id = 'mobileDisplayDock';
  dock.setAttribute('aria-label', 'Mobile display controls');

  const makeButton = (label: string, aria: string): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'mobile-display-button';
    button.textContent = label;
    button.setAttribute('aria-label', aria);
    dock.appendChild(button);
    return button;
  };

  const fullscreen = makeButton('⛶ FULL', 'Enter or exit fullscreen');
  const hudDown = makeButton('HUD −', 'Make the tactical HUD smaller');
  const hudValue = makeButton('', 'Current tactical HUD size');
  hudValue.disabled = true;
  const hudUp = makeButton('HUD +', 'Make the tactical HUD larger');
  const contrast = makeButton('COLOUR BOOST', 'Toggle brighter mobile colours');
  document.body.appendChild(dock);

  const isMobile = () => mobileQuery.matches;
  const applyContrast = () => {
    document.body.classList.toggle('mobile-contrast', isMobile() && saveManager.data.settings.mobileContrast);
    contrast.classList.toggle('is-active', saveManager.data.settings.mobileContrast);
  };
  const syncHud = () => {
    const scale = Phaser.Math.Clamp(saveManager.data.settings.hudScale ?? 1, 0.85, 1.15);
    saveManager.data.settings.hudScale = scale;
    hudValue.textContent = `HUD ${Math.round(scale * 100)}%`;
    hudDown.disabled = scale <= 0.85;
    hudUp.disabled = scale >= 1.15;
  };
  fullscreen.addEventListener('click', () => {
    audio.click();
    if (game.scale.isFullscreen) game.scale.stopFullscreen();
    else game.scale.startFullscreen();
  });
  hudDown.addEventListener('click', () => {
    audio.click();
    const scale = Math.max(0.85, (saveManager.data.settings.hudScale ?? 1) - 0.05);
    saveManager.data.settings.hudScale = scale;
    saveManager.save();
    syncHud();
    window.dispatchEvent(new CustomEvent('apogee-hud-scale', { detail: scale }));
  });
  hudUp.addEventListener('click', () => {
    audio.click();
    const scale = Math.min(1.15, (saveManager.data.settings.hudScale ?? 1) + 0.05);
    saveManager.data.settings.hudScale = scale;
    saveManager.save();
    syncHud();
    window.dispatchEvent(new CustomEvent('apogee-hud-scale', { detail: scale }));
  });
  contrast.addEventListener('click', () => {
    audio.click();
    saveManager.data.settings.mobileContrast = !saveManager.data.settings.mobileContrast;
    saveManager.save();
    applyContrast();
  });
  const syncFullscreen = () => fullscreen.textContent = game.scale.isFullscreen ? '⛶ EXIT' : '⛶ FULL';
  game.scale.on('fullscreenchange', syncFullscreen);
  mobileQuery.addEventListener?.('change', () => { applyContrast(); syncHud(); });
  applyContrast();
  syncHud();
}

// The wide black margins are part of the authored command-deck presentation.
// The Command Wing turns one of those margins into a quiet, useful telemetry
// rail without covering the game canvas or adding another in-game panel.
if (typeof document !== 'undefined') {
  const commandWing = document.createElement('aside');
  commandWing.id = 'commandWing';
  commandWing.setAttribute('aria-label', 'Command Wing telemetry');
  commandWing.innerHTML = [
    '<div class="command-wing-shell">',
    '  <div class="command-wing-kicker">SIDE CHANNEL // AEGIS</div>',
    '  <div class="command-wing-title">COMMAND WING</div>',
    '  <div class="command-wing-rule"></div>',
    '  <section class="command-wing-block command-wing-operation">',
    '    <div class="command-wing-label">NEXT OPERATION</div>',
    '    <div class="command-wing-value" data-wing="mission">SYNCING</div>',
    '    <div class="command-wing-meta" data-wing="missionMeta">OPERATIONS BOARD LINK</div>',
    '  </section>',
    '  <section class="command-wing-grid">',
    '    <div class="command-wing-block"><div class="command-wing-label">CAMPAIGN</div><div class="command-wing-value" data-wing="campaign">—</div></div>',
    '    <div class="command-wing-block"><div class="command-wing-label">COMMAND RANK</div><div class="command-wing-value" data-wing="rank">—</div></div>',
    '    <div class="command-wing-block"><div class="command-wing-label">RESEARCH DATA</div><div class="command-wing-value" data-wing="data">—</div></div>',
    '    <div class="command-wing-block"><div class="command-wing-label">BOARD STATE</div><div class="command-wing-value" data-wing="state">READY</div></div>',
    '  </section>',
    '  <div class="command-wing-rule"></div>',
    '  <div class="command-wing-status"><span class="command-wing-status-dot"></span><span data-wing="status">TACTICAL LINK STABLE</span></div>',
    '  <div class="command-wing-footer">OPEN OPERATIONS BOARD<br>DEPLOY WHEN READY</div>',
    '</div>',
  ].join('');
  document.body.appendChild(commandWing);

  const wingText = (key: string, value: string) => {
    const node = commandWing.querySelector(`[data-wing="${key}"]`);
    if (node) node.textContent = value;
  };
  const renderCommandWing = () => {
    const completed = saveManager.data.missionsComplete ?? [];
    const next = MISSIONS.find((mission) => (
      !completed.includes(mission.id)
      && isMissionUnlocked(mission.id, completed, saveManager.data.settings.devMode)
    ));
    const commander = saveManager.data.commanderName || 'ARC-CADET';
    wingText('mission', next ? next.name : 'CAMPAIGN CLEAR');
    wingText('missionMeta', next ? `${next.index.toString().padStart(2, '0')}  ·  ${next.unlocksNote}` : 'ALL OPERATIONS HELD  ·  AWAITING NEW ORDERS');
    wingText('campaign', `${completed.length}/${MISSIONS.length}`);
    wingText('rank', `${saveManager.data.rank}  ·  ${commander.slice(0, 12)}`);
    wingText('data', String(saveManager.data.researchPoints));
    wingText('state', next ? 'DEPLOY READY' : 'THEATRE CLEAR');
    wingText('status', next ? 'TACTICAL LINK STABLE' : 'CAMPAIGN ARCHIVE COMPLETE');
  };
  const syncCommandWing = () => {
    const rect = game.canvas?.getBoundingClientRect();
    if (!rect || window.innerWidth <= 1400) {
      commandWing.classList.remove('is-visible');
      commandWing.style.left = '';
      commandWing.style.right = '';
      return;
    }
    const leftSpace = Math.max(0, rect.left);
    const rightSpace = Math.max(0, window.innerWidth - rect.right);
    const wingWidth = 252;
    const gutter = 14;
    const useLeft = leftSpace >= rightSpace;
    const available = useLeft ? leftSpace : rightSpace;
    if (available < wingWidth + gutter) {
      commandWing.classList.remove('is-visible');
      commandWing.style.left = '';
      commandWing.style.right = '';
      return;
    }
    commandWing.classList.add('is-visible');
    commandWing.style.left = useLeft ? `${Math.max(10, (leftSpace - wingWidth) / 2)}px` : 'auto';
    commandWing.style.right = useLeft ? 'auto' : `${Math.max(10, (rightSpace - wingWidth) / 2)}px`;
  };
  renderCommandWing();
  window.addEventListener('apogee-command-wing-update', () => {
    renderCommandWing();
    syncCommandWing();
  });
  window.addEventListener('resize', syncCommandWing);
  const observeCommandWingCanvas = () => {
    const canvas = game.canvas;
    if (!canvas) {
      window.setTimeout(observeCommandWingCanvas, 120);
      return;
    }
    const ResizeObserverCtor = window.ResizeObserver;
    if (ResizeObserverCtor) {
      const observer = new ResizeObserverCtor(syncCommandWing);
      observer.observe(canvas);
    }
    syncCommandWing();
  };
  observeCommandWingCanvas();
  window.setTimeout(syncCommandWing, 0);
  window.setTimeout(syncCommandWing, 250);
  window.setTimeout(syncCommandWing, 1000);

// On wide displays, let the commander park the authored 16:9 game window to
// one side and reclaim the other black margin for another app/window. The
// arrows only reveal themselves while the pointer is over a usable margin.
if (typeof document !== 'undefined') {
  let viewportOffset = 0;
  const shiftStep = 160;
  let baseCanvasLeft: number | undefined;
  let baseCanvasWidth: number | undefined;
  let baseCanvasInitialized = false;
  const leftArrow = document.createElement('button');
  const rightArrow = document.createElement('button');
  const arrows = [[leftArrow, '‹', 'Move game window left'], [rightArrow, '›', 'Move game window right']] as const;
  arrows.forEach(([button, label, aria]) => {
    button.type = 'button';
    button.className = 'viewport-shift-arrow';
    button.textContent = label;
    button.setAttribute('aria-label', aria);
    document.body.appendChild(button);
  });
  const canvasRect = () => game.canvas?.getBoundingClientRect();
  const syncViewportShift = () => {
    const rect = canvasRect();
    if (!rect) return;
    syncCommandWing();
    if (!baseCanvasInitialized || baseCanvasLeft === undefined || baseCanvasWidth === undefined) {
      baseCanvasLeft = rect.left - viewportOffset;
      baseCanvasWidth = rect.width;
      baseCanvasInitialized = true;
    }
    const leftSpace = Math.max(0, rect.left);
    const rightSpace = Math.max(0, window.innerWidth - rect.right);
    const minOffset = -Math.floor(baseCanvasLeft);
    const maxOffset = Math.floor(window.innerWidth - (baseCanvasLeft + baseCanvasWidth));
    const dockedLeft = viewportOffset <= minOffset + 1;
    const dockedRight = viewportOffset >= maxOffset - 1;
    // Keep each control in the middle of its available black gutter. The
    // canvas can be narrower than the viewport, so placing the arrow directly
    // beside the canvas makes it look attached to the game instead of the
    // surrounding margin.
    const leftArrowWidth = leftArrow.offsetWidth || 34;
    const rightArrowWidth = rightArrow.offsetWidth || 34;
    leftArrow.style.left = `${Math.max(8, (leftSpace - leftArrowWidth) / 2)}px`;
    rightArrow.style.left = `${Math.min(
      window.innerWidth - rightArrowWidth - 8,
      rect.right + Math.max(0, (rightSpace - rightArrowWidth) / 2),
    )}px`;
    leftArrow.dataset.available = leftSpace > 34 && !dockedLeft ? 'true' : 'false';
    rightArrow.dataset.available = rightSpace > 34 && !dockedRight ? 'true' : 'false';
    if (leftArrow.dataset.available !== 'true') leftArrow.classList.remove('is-visible');
    if (rightArrow.dataset.available !== 'true') rightArrow.classList.remove('is-visible');
  };
  const setViewportShift = (next: number) => {
    const rect = canvasRect();
    if (!rect) return;
    const baseLeft = baseCanvasLeft ?? rect.left - viewportOffset;
    const baseWidth = baseCanvasWidth ?? rect.width;
    const minOffset = -Math.floor(baseLeft);
    const maxOffset = Math.floor(window.innerWidth - (baseLeft + baseWidth));
    viewportOffset = Phaser.Math.Clamp(next, minOffset, maxOffset);
    const app = document.getElementById('app');
    if (app) app.style.transform = `translate3d(${viewportOffset}px, 0, 0)`;
    syncViewportShift();
    syncCommandWing();
    window.setTimeout(syncViewportShift, 220);
  };
  leftArrow.addEventListener('click', () => { audio.click(); setViewportShift(viewportOffset - shiftStep); });
  rightArrow.addEventListener('click', () => { audio.click(); setViewportShift(viewportOffset + shiftStep); });
  document.addEventListener('pointermove', (event) => {
    const rect = canvasRect();
    if (!rect) return;
    const overLeftMargin = event.clientX < rect.left - 4;
    const overRightMargin = event.clientX > rect.right + 4;
    leftArrow.classList.toggle('is-visible', overLeftMargin && leftArrow.dataset.available === 'true');
    rightArrow.classList.toggle('is-visible', overRightMargin && rightArrow.dataset.available === 'true');
  });
  window.addEventListener('resize', () => { baseCanvasInitialized = false; syncViewportShift(); });
  game.events?.on?.('ready', syncViewportShift);
  window.setTimeout(syncViewportShift, 250);
}
}

// Browsers block audio until a user gesture. Prime the synths on the first
// interaction so the menu score starts the moment the player engages.
if (typeof window !== 'undefined') {
  const primeAudio = () => {
    audio.unlock();
    music.unlock();
    window.removeEventListener('pointerdown', primeAudio);
    window.removeEventListener('keydown', primeAudio);
  };
  window.addEventListener('pointerdown', primeAudio);
  window.addEventListener('keydown', primeAudio);
  // In case autoplay is permitted, try immediately too.
  try {
    audio.unlock();
    music.unlock();
  } catch {
    /* awaiting gesture */
  }
}

// Fullscreen toggle — F to fill the display, ESC-style exit handled by the browser.
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if (e.key === 'f' || e.key === 'F') {
      if (game.scale.isFullscreen) game.scale.stopFullscreen();
      else game.scale.startFullscreen();
    }
  });
}
